import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../hooks/apiRequest";
import { SessionUser } from "../types/interfaces";
import { userKeys } from "../utils/queryKeys";

export function useUsers(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: userKeys.list(),
    queryFn: async () => (await apiRequest<SessionUser[]>("users")).data,
    enabled: options.enabled ?? true,
  });
}

export interface StaffMember {
  _id: string;
  name: string;
  role: "manager" | "agent";
}

// id + name of active staff — readable by agents too (the full list is manager-only),
// used to populate assignee pickers.
export function useStaffDirectory(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: userKeys.directory(),
    queryFn: async () => (await apiRequest<StaffMember[]>("users/directory")).data,
    enabled: options.enabled ?? true,
  });
}

export interface CreateUserPayload {
  name: string;
  username: string;
  password?: string;
  authProvider?: "local" | "microsoft";
  role: SessionUser["role"];
  email?: string;
  assignedProjectIds?: string[];
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateUserPayload) =>
      (await apiRequest<SessionUser>("users", { method: "POST", body: JSON.stringify(data) })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}

type UpdateUserPayload = Partial<
  Pick<SessionUser, "name" | "username" | "email" | "role" | "active" | "assignedProjectIds">
>;

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateUserPayload }) =>
      (await apiRequest<SessionUser>(`users/${id}`, { method: "PUT", body: JSON.stringify(data) })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiRequest<void>(`users/${id}`, { method: "DELETE" })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}
